class CalendarVariantPicker {
  constructor(container) {
    this.container = container;
    this.productId = container.dataset.productId;
    this.currentDate = new Date();
    this.currentDate.setDate(1); // Always set to first of month
    this.selectedDate = null;
    this.variants = [];
    this.variantsByDate = {};
    
    this.init();
  }
  
  async init() {
    await this.fetchProductData();
    this.parseVariants();
    this.render();
    this.attachEvents();
  }
  
  async fetchProductData() {
    const response = await fetch(`/products/${this.getProductHandle()}.js`);
    const product = await response.json();
    this.variants = product.variants;
  }
  
  getProductHandle() {
    return window.location.pathname.split('/products/')[1].split('?')[0];
  }
  
parseVariants() {
  this.variants.forEach(variant => {
    const title = variant.title;
    
    // Set up today's date at midnight for consistent comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Try to parse date range format first (courses)
    // Format: "1 December - 15 December (Dec 1, 8, 15) (6:00PM - 9:00PM)" 
    const dateRangeMatch = title.match(/(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s*-\s*(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i);
    
    if (dateRangeMatch) {
      const startDay = dateRangeMatch[1];
      const startMonth = dateRangeMatch[2];
      const endDay = dateRangeMatch[3];
      const endMonth = dateRangeMatch[4];
      
      // Get current year or next year if the date has passed
      const currentYear = new Date().getFullYear();
      let date = new Date(`${startMonth} ${startDay}, ${currentYear}`);
      
      // If the date is before today (not including today), assume it's next year
      if (date < today) {
        date = new Date(`${startMonth} ${startDay}, ${currentYear + 1}`);
      }
      
      const dateKey = date.toISOString().split('T')[0];
      
      // Extract time range (get the LAST parentheses with time format)
      const timeMatch = title.match(/\((\d{1,2}:\d{2}[AP]M\s*-\s*\d{1,2}:\d{2}[AP]M)\)/);
      const timeDisplay = timeMatch ? timeMatch[1] : 'Time not specified';
      
      // Extract individual session dates if present (e.g., "Dec 1, 8, 15")
      const sessionDatesMatch = title.match(/\(([A-Za-z]+\s+[\d,\s|]+)\)\s*\(/);
      const sessionDates = sessionDatesMatch ? sessionDatesMatch[1].trim() : null;
      
      // Create display for date range
      const dateRangeDisplay = `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
      
      if (!this.variantsByDate[dateKey]) {
        this.variantsByDate[dateKey] = [];
      }
      
      this.variantsByDate[dateKey].push({
        id: variant.id,
        title: variant.title,
        available: variant.available,
        time: timeDisplay,
        dateRange: dateRangeDisplay,
        sessionDates: sessionDates,
        isCourse: true,
        fullTitle: title
      });
      
      return; // Exit early if we matched date range format
    }
    
    // Try multi-date format (like "Group 1: Dec 4,11,18")
    const multiDateMatch = title.match(/(\w+)\s+(\d{1,2})[,\d\s]*/);
    
    if (multiDateMatch && title.includes(',')) {
      const firstMonth = multiDateMatch[1];
      const firstDay = multiDateMatch[2];
      
      const currentYear = new Date().getFullYear();
      let date = new Date(`${firstMonth} ${firstDay}, ${currentYear}`);
      
      // If the date is before today (not including today), assume it's next year
      if (date < today) {
        date = new Date(`${firstMonth} ${firstDay}, ${currentYear + 1}`);
      }
      
      const dateKey = date.toISOString().split('T')[0];
      
      const timeMatch = title.match(/\((\d{1,2}:\d{2}[AP]M\s*-\s*\d{1,2}:\d{2}[AP]M)\)/);
      const timeDisplay = timeMatch ? timeMatch[1] : 'Time not specified';
      
      // Extract the date list for display
      const dateListMatch = title.match(/([A-Za-z]+\s+[\d,]+)\s*\(/);
      const dateRangeDisplay = dateListMatch ? dateListMatch[1].trim() : '';
      
      if (!this.variantsByDate[dateKey]) {
        this.variantsByDate[dateKey] = [];
      }
      
      this.variantsByDate[dateKey].push({
        id: variant.id,
        title: variant.title,
        available: variant.available,
        time: timeDisplay,
        dateRange: dateRangeDisplay,
        sessionDates: null,
        isCourse: true,
        fullTitle: title
      });
      
      return;
    }
    
    // Fall back to single-date format (workshops/classes)
    // Format: "Thu 27 November (6:00PM - 8:00PM) Adults Only with Flora"
    const singleDateMatch = title.match(/(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)/i);
    
    if (singleDateMatch) {
      const day = singleDateMatch[1];
      const month = singleDateMatch[2];
      
      const currentYear = new Date().getFullYear();
      let date = new Date(`${month} ${day}, ${currentYear}`);
      
      // If the date is before today (not including today), assume it's next year
      if (date < today) {
        date = new Date(`${month} ${day}, ${currentYear + 1}`);
      }
      
      const dateKey = date.toISOString().split('T')[0];
      
      const timeMatch = title.match(/\(([^)]+)\)/);
      const timeDisplay = timeMatch ? timeMatch[1] : 'Time not specified';
      
      if (!this.variantsByDate[dateKey]) {
        this.variantsByDate[dateKey] = [];
      }
      
      this.variantsByDate[dateKey].push({
        id: variant.id,
        title: variant.title,
        available: variant.available,
        time: timeDisplay,
        dateRange: null,
        sessionDates: null,
        isCourse: false,
        fullTitle: title
      });
    }
  });
}

  
  render() {
    this.renderCalendar();
  }
  
  renderCalendar() {
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();
    
    // Update header
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
    this.container.querySelector('.calendar-month').textContent = 
      `${monthNames[month]} ${year}`;
    
    // Build calendar grid
    const grid = this.container.querySelector('.calendar-grid');
    grid.innerHTML = '';
    
    // Day headers
    const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    dayHeaders.forEach(day => {
      const header = document.createElement('div');
      header.className = 'calendar-day-header';
      header.textContent = day;
      grid.appendChild(header);
    });
    
    // Get first day of month and number of days
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      const empty = document.createElement('div');
      grid.appendChild(empty);
    }
    
    // Day cells
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateKey = date.toISOString().split('T')[0];
      const hasVariants = this.variantsByDate[dateKey]?.length > 0;
      
      const dayCell = document.createElement('button');
      dayCell.type = 'button';
      dayCell.className = 'calendar-day';
      dayCell.textContent = day;
      dayCell.dataset.date = dateKey;
      
      if (hasVariants) {
        dayCell.classList.add('has-variants');
      } else {
        dayCell.classList.add('disabled');
        dayCell.disabled = true;
      }
      
      if (date < today) {
        dayCell.classList.add('disabled');
        dayCell.disabled = true;
      }
      
      grid.appendChild(dayCell);
    }
  }
  
renderTimeSlots(dateKey) {
  const timeSlotsContainer = this.container.querySelector('.time-slots');
  const buttonsContainer = this.container.querySelector('.time-slot-buttons');
  
  buttonsContainer.innerHTML = '';
  
  const variants = this.variantsByDate[dateKey] || [];
  
  variants.forEach(variant => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'time-slot-button';
    
    // Build the button content
    let buttonHTML = '';
    
    // If it's a course, show the date range
    if (variant.isCourse && variant.dateRange) {
      buttonHTML += `<div class="time-slot-dates">${variant.dateRange}</div>`;
    }
    
    // Show time
    buttonHTML += `<div class="time-slot-time">${variant.time}</div>`;
    
    // Only extract "Adults Only" - nothing else
    const isAdultsOnly = variant.fullTitle.includes('Adults Only');
    
    // Extract instructor name "with [Name]"
    const instructorMatch = variant.fullTitle.match(/with\s+([A-Za-z]+)/i);
    const instructor = instructorMatch ? `with ${instructorMatch[1]}` : '';
    
    // Extract week count for courses
    const weekMatch = variant.fullTitle.match(/(\d+-Week)/i);
    const weekInfo = weekMatch ? weekMatch[1] : '';
    
    if (isAdultsOnly) {
      buttonHTML += `<div class="time-slot-age">Adults Only</div>`;
    }
    
    if (weekInfo) {
      buttonHTML += `<div class="time-slot-course-info">${weekInfo}</div>`;
    }
    
    if (instructor) {
      buttonHTML += `<div class="time-slot-instructor">${instructor}</div>`;
    }
    
    button.innerHTML = buttonHTML;
    button.setAttribute('data-variant-id', variant.id);
    button.title = variant.fullTitle;
    
    if (!variant.available) {
      button.classList.add('sold-out');
      button.disabled = true;
      button.innerHTML += '<div class="time-slot-soldout">(Sold Out)</div>';
    }
    
    buttonsContainer.appendChild(button);
  });
  
  timeSlotsContainer.style.display = variants.length > 0 ? 'block' : 'none';
}

  
  attachEvents() {
    // Remove old event listeners by cloning and replacing nav buttons
    const navButtons = this.container.querySelectorAll('.calendar-nav');
    navButtons.forEach(oldBtn => {
      const newBtn = oldBtn.cloneNode(true);
      oldBtn.parentNode.replaceChild(newBtn, oldBtn);
    });
    
    // Navigation with fresh event listeners
    this.container.querySelectorAll('.calendar-nav').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const direction = e.target.dataset.direction;
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
        
        if (direction === 'prev') {
          this.currentDate = new Date(year, month - 1, 1);
        } else {
          this.currentDate = new Date(year, month + 1, 1);
        }
        
        this.render();
        this.attachEvents();
      });
    });
    
    // Date selection
    this.container.querySelectorAll('.calendar-day.has-variants').forEach(day => {
      day.addEventListener('click', (e) => {
        this.container.querySelectorAll('.calendar-day').forEach(d => 
          d.classList.remove('selected'));
        e.target.classList.add('selected');
        this.selectedDate = e.target.dataset.date;
        this.renderTimeSlots(this.selectedDate);
        this.attachTimeSlotEvents();
      });
    });
  }
  
attachTimeSlotEvents() {
  const buttons = this.container.querySelectorAll('.time-slot-button:not(.sold-out)');
  
  buttons.forEach(btn => {
    // Remove any existing listeners by cloning
    const newBtn = btn.cloneNode(true);
    btn.parentNode.replaceChild(newBtn, btn);
  });
  
  // Re-select buttons after cloning
  this.container.querySelectorAll('.time-slot-button:not(.sold-out)').forEach(btn => {
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      // Get the button (this works better in Safari)
      const button = this;
      
      // Update visual selection
      document.querySelectorAll('.time-slot-button').forEach(function(b) {
        b.classList.remove('selected');
      });
      button.classList.add('selected');
      
      const variantId = button.getAttribute('data-variant-id');
      
      // Update hidden input
      const hiddenInput = document.querySelector('.selected-variant-id');
      if (hiddenInput) {
        hiddenInput.value = variantId;
      }
      
      // Find and update the product form
      const productForm = document.querySelector('form[action*="/cart/add"]');
      if (productForm) {
        const variantInput = productForm.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variantId;
        }
        
        // Safari-compatible event triggering
        const changeEvent = document.createEvent('HTMLEvents');
        changeEvent.initEvent('change', true, false);
        productForm.dispatchEvent(changeEvent);
      }
      
      // Update URL
      if (window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      }
    }, false);
  });
}

}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  const calendarPickers = document.querySelectorAll('.calendar-variant-picker');
  calendarPickers.forEach(picker => new CalendarVariantPicker(picker));
});
